import { inject, Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { NGXLogger } from "ngx-logger";
import { formatCurrency, formatNumber } from "@angular/common";
import {
  Box,
  Category,
  BoxPaperType,
  BoxPrintType,
  BoxType,
  ENVIRONMENT,
  Preset,
  PricingResponse,
  Subcategory,
  Variant,
} from "@company-box/models";

@Injectable({
  providedIn: "root",
})
export class BoxService {
  private environment = inject(ENVIRONMENT)
  constructor(protected httpClient: HttpClient, private logger: NGXLogger) {}

  // Values that mean "absent" for URL-building purposes: a real undefined/null, or the literal
  // strings "undefined"/"null" that Angular's router bakes into route params when a matrix param
  // was built from an object with an undefined/null value (Router stringifies every present key).
  private isAbsent(value: any): boolean {
    return value === undefined || value === null || value === "undefined" || value === "null";
  }

  // Path segments are always required by the route, so an absent value becomes an empty segment
  // rather than the literal text "undefined".
  private seg(value: any): string {
    return this.isAbsent(value) ? "" : String(value);
  }

  // Query params are optional: an absent value is omitted entirely rather than sent as the literal
  // text "undefined".
  private query(params: Record<string, any>): string {
    const entries = Object.entries(params).filter(([, value]) => !this.isAbsent(value));
    if (entries.length === 0) {
      return "";
    }
    return "?" + entries.map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`).join("&");
  }

  getPrice(
    boxCode: string,
    boardStrength: string,
    paperType: string,
    length: number,
    width: number,
    depth: number,
    quantity: number,
    twoSided: boolean,
    luxePrint: boolean,
    gloss: boolean,
    noPrint: boolean,
    options: string,
    expedited: boolean
  ): Promise<Box> {
    return new Promise<Box>((resolve, reject) => {
      const params: Record<string, any> = { length, width, depth, quantity, rush: expedited, debug: true };
      if (noPrint) {
        params["noPrint"] = noPrint;
      } else {
        params["twoSided"] = twoSided;
        params["luxe"] = luxePrint;
        params["gloss"] = gloss;
        params["options"] = options;
      }
      const url = `${this.environment.apiHost}/box/price/${this.seg(boxCode)}/${this.seg(boardStrength)}/${this.seg(
        paperType
      )}${this.query(params)}`;

      this.httpClient.get(url).subscribe(
        (res) => {
          const price = new Box().deserialize(res);
          this.logger.debug("price", price);
          resolve(price);
        },
        (error) => {
          this.logger.debug(error.message || error);
          reject(error.message || error);
        }
      );
    });
  }

  getSamplePrice(
    code: any,
    boardStrength: any,
    kraftType: any,
    width?: any,
    length?: any,
    depth?: any,
    gloss?: any,
    luxe?: any,
    twoSided?: any,
    options?: any,
    quantity?: any,
    rush?: any
  ) {
    return new Promise((resolve, reject) => {
      const url = `${this.environment.apiHost}/box/price/${this.seg(code)}/${this.seg(boardStrength)}/${this.seg(
        kraftType
      )}${this.query({ width, length, depth, gloss, luxe, twoSided, quantity, options, rush })}`;
      this.httpClient.get(url).subscribe(
        (response) => {
          resolve(response);
        },
        (error) => {
          reject("Could not get price");
        }
      );
    });
  }

  listPrices(
    boxCode: string,
    boardStrength: string,
    paperType: string,
    length: number,
    width: number,
    depth: number,
    quantities: number[],
    twoSided: boolean,
    luxePrint: boolean,
    noPrint: boolean,
    options: string,
    expedited: boolean
  ): Promise<PricingResponse> {
    return new Promise<PricingResponse>((resolve, reject) => {
      const params: Record<string, any> = {
        length,
        width,
        depth,
        quantities: quantities.join(","),
        rush: expedited,
        includeSubcategories: true,
        options,
        debug: true,
      };
      if (noPrint) {
        params["noPrint"] = noPrint;
      } else {
        params["twoSided"] = twoSided;
        params["luxe"] = luxePrint;
      }
      const url = `${this.environment.apiHost}/box/price/${this.seg(boxCode)}/${this.seg(boardStrength)}/${this.seg(
        paperType
      )}/list${this.query(params)}`;

      this.httpClient.get(url).subscribe(
        (res) => {
          const response = new PricingResponse().deserialize(res);
          resolve(response);
        },
        (error) => {
          this.logger.debug(error.message || error);
          reject(error.message || error);
        }
      );
    });
  }

  listTissuePrices(quantities: number[], weight: number) {
    const url = `${this.environment.apiHost}/box/price/tissue/White/${this.seg(weight)}/list${this.query({
      quantities: quantities.join(","),
      rush: false,
    })}`;

    return new Promise((resolve, reject) => {
      this.httpClient.get(url).subscribe(
        (res) => resolve(res),
        (error) => reject(error)
      );
    });
  }

  getTissuePrice(quantity: number, weight: number) {
    const url = `${this.environment.apiHost}/box/price/tissue/White/${this.seg(weight)}${this.query({
      quantity,
      rush: false,
    })}`;

    return new Promise((resolve, reject) => {
      this.httpClient.get(url).subscribe(
        (res) => resolve(res),
        (error) => reject(error)
      );
    });
  }

  listCrinklePrices(quantities: number[], color: string) {
    const encodedColor = encodeURIComponent(String(color ?? "").trim().toLowerCase());
    const url = `${this.environment.apiHost}/box/price/crinkle/${encodedColor}/list${this.query({
      quantities: quantities.join(","),
    })}`;

    return new Promise((resolve, reject) => {
      this.httpClient.get(url).subscribe(
        (res) => resolve(res),
        (error) => reject(error)
      );
    });
  }

  getCrinklePrice(quantity: number, color: string) {
    const encodedColor = encodeURIComponent(String(color ?? "").trim().toLowerCase());
    const url = `${this.environment.apiHost}/box/price/crinkle/${encodedColor}${this.query({ quantity })}`;

    return new Promise((resolve, reject) => {
      this.httpClient.get(url).subscribe(
        (res) => resolve(res),
        (error) => reject(error)
      );
    });
  }

  listPaddedMailerPrices(code: string, paperType: string, quantities: number[], noPrint: boolean) {
    const url = `${this.environment.apiHost}/box/price/padded-mailer/${this.seg(code)}/${this.seg(
      paperType
    )}/list${this.query({ quantities: quantities.join(","), rush: false, noPrint })}`;

    return new Promise((resolve, reject) => {
      this.httpClient.get(url).subscribe(
        (response: any) => {
          (response.prices as any[]).forEach((price) => {
            this.setPaddedMailerPriceLabel(price, response.paddedMailer.type.subcategory.allowCustomSample);
          });
          resolve(response);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  getPaddedMailerPrice(code: string, paperType: string, quantity: number, noPrint: boolean) {
    const url = `${this.environment.apiHost}/box/price/padded-mailer/${this.seg(code)}/${this.seg(
      paperType
    )}${this.query({ quantity, rush: false, noPrint })}`;

    return new Promise((resolve, reject) => {
      this.httpClient.get(url).subscribe(
        (response: any) => {
          this.setPaddedMailerPriceLabel(response.price, response.type.subcategory.allowCustomSample);
          resolve(response);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  setPaddedMailerPriceLabel(
    price: {
      quantity: number;
      label: string;
      pricePerUnit: number;
      unitsPerCase: string;
    },
    allowCustomSample: any
  ) {
    // console.log(`q ${price.quantity} ${price.quantity === 1 && subcategory.allowCustomSample}`);
    if (price.quantity === 1 && allowCustomSample) {
      price.label = `1 Custom Sample ${formatCurrency(price.pricePerUnit, "en-us", "$")}`;
    } else {
      price.label =
        formatNumber(price.quantity, "en-us") +
        " case(s) of " +
        price.unitsPerCase +
        " " +
        formatCurrency(price.pricePerUnit, "en-us", "$") +
        " / Mailer";
    }
  }

  getType(code: string): Promise<BoxType> {
    return new Promise<BoxType>((resolve, reject) => {
      const url = `${this.environment.apiHost}/box/type/${this.seg(code)}`;
      this.httpClient.get(url).subscribe(
        (res) => {
          const box = new BoxType().deserialize(res);
          this.logger.debug("box", box);
          resolve(box);
        },
        (error) => {
          this.logger.debug(error.message || error);
          reject(error.message || error);
        }
      );
    });
  }

  listTypes(): Promise<BoxType[]> {
    return new Promise<BoxType[]>((resolve, reject) => {
      const url = `${this.environment.apiHost}/box/type/all`;
      this.httpClient.get(url).subscribe(
        (res) => {
          const boxes: BoxType[] | PromiseLike<BoxType[]> = [];
          (res as any[]).forEach((value) => {
            const box = new BoxType().deserialize(value);
            boxes.push(box);
          });
          this.logger.debug("boxes", boxes);
          resolve(boxes);
        },
        (error) => {
          this.logger.debug(error.message || error);
          reject(error.message || error);
        }
      );
    });
  }

  getCategory(categoryCode: string, includeSubcategories: boolean): Promise<Category> {
    return new Promise<Category>((resolve, reject) => {
      const url = `${this.environment.apiHost}/box/category/${this.seg(categoryCode)}${this.query(
        includeSubcategories ? { includeCategories: true } : {}
      )}`;
      this.httpClient.get(url).subscribe(
        (res) => {
          const category = new Category().deserialize(res);
          this.logger.debug("category", category);
          resolve(category);
        },
        (error) => {
          this.logger.debug(error.message || error);
          reject(error.message || error);
        }
      );
    });
  }
  getSubcategory(subcategoryCode: string): Promise<Subcategory> {
    return new Promise<Subcategory>((resolve, reject) => {
      const url = `${this.environment.apiHost}/box/category/subcategory/${this.seg(subcategoryCode)}`;
      this.httpClient.get(url).subscribe(
        (res) => {
          const subcategory = new Subcategory().deserialize(res);
          this.logger.debug("subcategory", subcategory);
          resolve(subcategory);
        },
        (error) => {
          this.logger.debug(error.message || error);
          reject(error.message || error);
        }
      );
    });
  }

  listCategories(includeSubcategories: boolean): Promise<Category[]> {
    return new Promise<Category[]>((resolve, reject) => {
      const url = `${this.environment.apiHost}/box/category/list${this.query(
        includeSubcategories ? { includeCategories: true } : {}
      )}`;

      this.httpClient.get(url).subscribe(
        (res) => {
          const categories: Category[] | PromiseLike<Category[]> = [];
          (res as any[]).forEach((value) => {
            const category = new Category().deserialize(value);
            categories.push(category);
          });
          // this.logger.debug('categories', categories);
          resolve(categories);
        },
        (error) => {
          this.logger.debug(error.message || error);
          reject(error.message || error);
        }
      );
    });
  }

  listPresets(categoryCode: string, subcategoryCode: string): Promise<Preset[]> {
    return new Promise<Preset[]>((resolve, reject) => {
      const url = `${this.environment.apiHost}/box/category/${this.seg(categoryCode)}/${this.seg(
        subcategoryCode
      )}/presets/list${this.query({ includeSubcategories: true })}`;
      this.httpClient.get(url).subscribe(
        (res) => {
          const presets: Preset[] | PromiseLike<Preset[]> = [];
          (res as any[]).forEach((value) => {
            const preset = new Preset().deserialize(value);
            presets.push(preset);
          });
          // this.logger.debug('presets', presets);
          resolve(presets);
        },
        (error) => {
          this.logger.debug(error.message || error);
          reject(error.message || error);
        }
      );
    });
  }

  getPaperTypes(selectedSubcategory: Subcategory): BoxPaperType[] {
    const paperTypes = [];
    if (selectedSubcategory?.displayPremiumWhite) {
      const paperType = new BoxPaperType().deserialize({
        id: "pkw",
        name: "Premium White",
        finishes: ["Gloss", "Matte"],
      });
      if (selectedSubcategory?.displayStandard) {
        paperType.printTypes.push(
          new BoxPrintType().deserialize({
            id: "standard",
            name: "Standard",
            allowed: true,
          })
        );
      }
      if (selectedSubcategory?.displayLuxe) {
        paperType.printTypes.push(
          new BoxPrintType().deserialize({
            id: "luxe",
            name: "Luxe",
            allowed: true,
          })
        );
      }
      paperTypes.push(paperType);
    }
    if (selectedSubcategory?.displayWhite) {
      const paperType = new BoxPaperType().deserialize({
        id: "kw",
        name: "Standard White",
        finishes: ["Matte"],
      });
      if (selectedSubcategory?.displayStandard) {
        paperType.printTypes.push(
          new BoxPrintType().deserialize({
            id: "standard",
            name: "Standard",
            allowed: true,
          })
        );
      }
      if (selectedSubcategory?.displayLuxe) {
        paperType.printTypes.push(
          new BoxPrintType().deserialize({
            id: "luxe",
            name: "Luxe",
            allowed: true,
          })
        );
      }
      paperTypes.push(paperType);
    }
    if (selectedSubcategory?.displayKraft) {
      const paperType = new BoxPaperType().deserialize({
        id: "k",
        name: "Kraft",
        finishes: ["Matte"],
      });
      if (selectedSubcategory?.displayStandard) {
        paperType.printTypes.push(
          new BoxPrintType().deserialize({
            id: "standard",
            name: "Standard",
            allowed: true,
          })
        );
      }
      if (selectedSubcategory?.displayLuxe) {
        paperType.printTypes.push(
          new BoxPrintType().deserialize({
            id: "luxe",
            name: "Luxe",
            allowed: false,
          })
        );
      }
      paperTypes.push(paperType);
    }
    paperTypes.forEach((paperType) => {
      if (selectedSubcategory.displaySecondaryWhite) {
        paperType.secondary.push("White");
      }
      if (selectedSubcategory.displaySecondaryKraft) {
        paperType.secondary.push("Kraft");
      }
    });
    return paperTypes;
  }

  getVariants(subcategoryId: number) {
    const url = `${this.environment.apiHost}/box/category/subcategory/${this.seg(
      subcategoryId
    )}/list${this.query({ includeSubcategory: true })}`;

    return new Promise<any>((resolve, reject) => {
      this.httpClient.get(url).subscribe(
        (res) => {
          const variants: Variant[] = [];
          (res as any[]).forEach((value) => {
            const variant = new Variant().deserialize(value);
            variants.push(variant);
          });
          resolve(variants);
        },
        (error) => {
          this.logger.debug(error.message || error);
          reject(error.message || error);
        }
      );
    });
  }
}
